import React from 'react';
import placeholderImage from '../../../assets/placeholder.png';

const STATUS_COLORS = {
  active: '#7bfac3',
  inactive: '#979797',
  new: '#FFB538'
};

const PLACEHOLDER_IMAGE = placeholderImage;

// Product image cell component
const ProductImageCell = ({ value }) => {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%'
    }}>
      {value ? (
        <img
          src={value}
          alt="Product"
          style={{
            width: 36,
            height: 36,
            objectFit: 'contain',
            borderRadius: 4,
            backgroundColor: '#2d3e4e'
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = PLACEHOLDER_IMAGE;
          }}
        />
      ) : (
        <div style={{
          width: 36,
          height: 36,
          borderRadius: 4,
          backgroundColor: '#3f5163',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10,
          color: '#869db1'
        }}>
          N/A
        </div>
      )}
    </div>
  );
};

// Boolean cell renderer
const BooleanCell = ({ value }) => {
  const isOn = value === 'on' || value === true;
  return (
    <span style={{
      color: isOn ? '#7bfac3' : '#979797',
      fontWeight: '500'
    }}>
      {isOn ? 'On' : 'Off'}
    </span>
  );
};

// Column definitions for the product grid
export const productGridColumns = [
  // Visible by default
  {
    name: 'image',
    header: 'Image',
    defaultWidth: 100,
    minWidth: 100,
    sortable: false,
    textAlign: 'center',
    render: ({ value }) => <ProductImageCell value={value} />
  },
  {
    name: 'name',
    header: 'Product Name',
    defaultFlex: 2,
    minWidth: 180
  },
  {
    name: 'sku',
    header: 'SKU',
    defaultFlex: 1,
    minWidth: 100
  },
  {
    name: 'qty_on_hand',
    header: 'QOH',
    defaultWidth: 80,
    type: 'number',
    textAlign: 'center'
  },
  {
    name: 'status',
    header: 'Status',
    defaultWidth: 100,
    render: ({ value }) => {
      const color = STATUS_COLORS[value] || '#fff';
      const displayValue = value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
      return (
        <span style={{ color, fontWeight: '500', fontFamily: 'Poppins_300Light' }}>
          {displayValue}
        </span>
      );
    }
  },
  {
    name: 'barcode',
    header: 'Barcodes',
    defaultFlex: 1,
    minWidth: 150
  },
  // Hidden by default
  {
    name: 'id',
    header: 'ID',
    defaultWidth: 80,
    type: 'number',
    defaultVisible: false
  },
  {
    name: 'location_primary',
    header: 'Primary Location',
    defaultWidth: 150,
    defaultVisible: false
  },
  {
    name: 'location_secondary',
    header: 'Secondary Location',
    defaultWidth: 150,
    defaultVisible: false
  },
  {
    name: 'location_tertiary',
    header: 'Tertiary Location',
    defaultWidth: 150,
    defaultVisible: false
  },
  {
    name: 'location_name',
    header: 'Warehouse',
    defaultWidth: 150,
    defaultVisible: false
  },
  {
    name: 'type_scan_enabled',
    header: 'Type Scan',
    defaultWidth: 100,
    defaultVisible: false,
    render: ({ value }) => <BooleanCell value={value} />
  },
  {
    name: 'click_scan_enabled',
    header: 'Click Scan',
    defaultWidth: 100,
    defaultVisible: false,
    render: ({ value }) => <BooleanCell value={value} />
  },
  {
    name: 'qty',
    header: 'Quantity',
    defaultWidth: 100,
    type: 'number',
    textAlign: 'center',
    defaultVisible: false
  },
  {
    name: 'cat',
    header: 'Category',
    defaultWidth: 120,
    defaultVisible: false
  },
  {
    name: 'custom_product_1',
    header: 'Custom Field 1',
    defaultWidth: 150,
    defaultVisible: false
  },
  {
    name: 'custom_product_2',
    header: 'Custom Field 2',
    defaultWidth: 150,
    defaultVisible: false
  },
  {
    name: 'custom_product_3',
    header: 'Custom Field 3',
    defaultWidth: 150,
    defaultVisible: false
  },
  {
    name: 'store_name',
    header: 'Store',
    defaultWidth: 150,
    defaultVisible: false
  },
  {
    name: 'available_inv',
    header: 'Available Inventory',
    defaultWidth: 120,
    type: 'number',
    textAlign: 'center',
    defaultVisible: false
  }
];

// Edit action options
export const editActionOptions = [
  { label: 'Delete', value: 'delete' },
  { label: 'Duplicate', value: 'duplicate' },
  { label: 'Generate barcode from SKU', value: 'generate_barcode_sku' },
  { label: 'Generate Numeric Barcode', value: 'generate_numeric_barcode' },
  { label: 'Print Product Barcode Labels', value: 'print_barcode_labels' },
  { label: 'Print Receiving Labels', value: 'print_receiving_labels' },
  { label: 'Export Products to CSV', value: 'export_csv' },
  { label: 'Broken/Missing image export', value: 'broken_image_export' }
];

// Status change options
export const statusOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'New', value: 'new' }
];

// Filter options for product status
export const productFilterOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'New', value: 'new' },
  { label: 'All', value: 'all' }
];

// Type filter options (Show All, Kits only, Products only)
export const typeFilterOptions = [
  { label: 'Show All', value: 'all' },
  { label: 'Kits', value: 'kits' },
  { label: 'Products', value: 'products' }
];

// Bulk actions configuration
export const productBulkActions = [
  {
    label: 'Edit',
    actionType: 'edit',
    options: editActionOptions
  },
  {
    label: 'Change Status',
    actionType: 'changeStatus',
    options: statusOptions
  }
];

